import type {
  ContractEntityWithValidation,
  MeterEntityWithValidation,
  MeterReadingValidation
} from '../../../utils/clients/entity-client'

import type {
  DynamicMeterReadingFormValues,
  MeterReadingDynamicFields
} from './types'

/**
 * check single meter data validity
 */
export function isMeterDataValid(
  meterData: DynamicMeterReadingFormValues,
  fields: MeterReadingDynamicFields,
  meterCountersLength?: number,
  meterValidations?: MeterReadingValidation[]
) {
  // if the data is missing meter id, then invalid
  if (!meterData.meterId) {
    return false
  }
  // if any of the required fields are missing, then invalid
  if (fields.readBy?.required && !meterData.readBy) {
    return false
  }
  if (fields.readingDate?.required && !meterData.readingDate) {
    return false
  }
  if (fields.reason?.required && !meterData.reason) {
    return false
  }

  // if there are no readings or not all readings are filled, then invalid
  if (
    !meterData.readings ||
    meterData.readings.length !== meterCountersLength
  ) {
    return false
  }

  // if any of the readings are missing a value, then invalid
  if (meterData.readings.find((r) => !r.value)) {
    return false
  }

  // if any of the readings are outside the min/max range, then invalid
  if (
    meterValidations?.some((validation) => {
      const reading = meterData?.readings?.find(
        (r) => r.counterId === validation.counter_id
      )

      if (!reading) {
        return false
      }

      if (
        typeof validation.min_value === 'number' &&
        reading.value < validation.min_value
      ) {
        return true
      }

      return false
    })
  ) {
    return false
  }

  return true
}

/**
 * check full block data validity
 */
export function isMeterBlockDataValid(
  newData?: DynamicMeterReadingFormValues[],
  contracts?: ContractEntityWithValidation[]
) {
  // get all meter ids from contract and make sure there are no duplicates (unique)
  const allMeterIds = contracts?.flatMap((c) => c.meters).map((m) => m._id) // get all meters from all contracts
  const uniqueMeterIdsSet = new Set(allMeterIds)

  return (
    newData?.length === uniqueMeterIdsSet.size &&
    newData.every((m) => m._isValid)
  )
}

/**
 * some contracts might share the meter and have the same name
 */
export function getUniqueContractNames(
  contracts: ContractEntityWithValidation[]
) {
  const contractNames = contracts.map((c) => c.contract_name)

  return Array.from(new Set(contractNames))
}

export function getUniqueContractMeters(
  contracts: ContractEntityWithValidation[]
) {
  const meters: MeterEntityWithValidation[] = []

  contracts.forEach((contract) => {
    contract.meters.forEach((meter) => {
      if (!meters.find((m) => m._id === meter._id)) {
        meters.push(meter)
      }
    })
  })

  return meters
}
